import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { createTenantStorage } from './tenantStorage';

export type BottomPanelTab = 'terminal' | 'code-graph' | 'decoder';

type BottomPanelState = {
  isOpen: boolean;
  selectedTab: BottomPanelTab;
  height: number;
  splitterSizes?: [number, number];
  currentEndpointId?: string;
  selectedFileId?: string;
  open: () => void;
  close: () => void;
  toggle: () => void;
  setTab: (tab: BottomPanelTab) => void;
  setHeight: (height: number) => void;
  setSplitterSizes: (sizes: [number, number]) => void;
  setCurrentEndpointId: (endpointId?: string) => void;
  setSelectedFileId: (fileId?: string) => void;
};

type PersistedBottomPanelState = Pick<
  BottomPanelState,
  'selectedTab' | 'height' | 'splitterSizes'
>;

export const DEFAULT_BOTTOM_PANEL_SPLITTER_SIZES: [number, number] = [75, 25];
export const MIN_RESTORED_BOTTOM_PANEL_SIZE = 15;

const BOTTOM_PANEL_PERSIST_VERSION = 2;
const BOTTOM_PANEL_STORE_NAME = 'terra-proxy-bottom-panel-state';
const LAYOUT_STORE_NAME = 'terra-proxy-layout-state';
const BOTTOM_PANEL_LAYOUT_MIGRATION_KEY =
  'terra-proxy-bottom-panel-layout-migration-v1';

function isBottomPanelTab(value: unknown): value is BottomPanelTab {
  return value === 'terminal' || value === 'code-graph' || value === 'decoder';
}

function getRestoredSplitterSizes(
  splitterSizes?: [number, number],
): [number, number] {
  if (!splitterSizes || splitterSizes[1] < MIN_RESTORED_BOTTOM_PANEL_SIZE) {
    return DEFAULT_BOTTOM_PANEL_SPLITTER_SIZES;
  }

  return splitterSizes;
}

function isSplitterSizes(value: unknown): value is [number, number] {
  return (
    Array.isArray(value) &&
    value.length === 2 &&
    typeof value[0] === 'number' &&
    typeof value[1] === 'number'
  );
}

function migratePersistedBottomPanelState(
  persistedState: unknown,
): PersistedBottomPanelState {
  const state =
    persistedState &&
    typeof persistedState === 'object' &&
    !Array.isArray(persistedState)
      ? (persistedState as Record<string, unknown>)
      : {};

  return {
    selectedTab: isBottomPanelTab(state.selectedTab)
      ? state.selectedTab
      : 'terminal',
    height: typeof state.height === 'number' ? state.height : 300,
    splitterSizes: isSplitterSizes(state.splitterSizes)
      ? state.splitterSizes
      : DEFAULT_BOTTOM_PANEL_SPLITTER_SIZES,
  };
}

function readPersistedState(
  rawValue: string | null,
): Record<string, unknown> | null {
  if (!rawValue) return null;

  try {
    const parsed = JSON.parse(rawValue) as unknown;
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      const state = (parsed as { state?: unknown }).state;
      if (state && typeof state === 'object' && !Array.isArray(state)) {
        return state as Record<string, unknown>;
      }
    }
  } catch {
    return null;
  }

  return null;
}

function getStorageItem(name: string): string | null {
  const value = createTenantStorage().getItem(name);
  return typeof value === 'string' ? value : null;
}

export const useBottomPanelStore = create<BottomPanelState>()(
  persist(
    (set) => ({
      isOpen: false,
      selectedTab: 'terminal',
      height: 300,
      splitterSizes: DEFAULT_BOTTOM_PANEL_SPLITTER_SIZES,
      currentEndpointId: undefined,
      selectedFileId: undefined,
      open: () =>
        set((state) => ({
          isOpen: true,
          splitterSizes: getRestoredSplitterSizes(state.splitterSizes),
        })),
      close: () => set({ isOpen: false }),
      toggle: () =>
        set((state) => ({
          isOpen: !state.isOpen,
          splitterSizes: state.isOpen
            ? state.splitterSizes
            : getRestoredSplitterSizes(state.splitterSizes),
        })),
      setTab: (tab) =>
        set((state) => ({
          selectedTab: tab,
          isOpen: true,
          splitterSizes: state.isOpen
            ? state.splitterSizes
            : getRestoredSplitterSizes(state.splitterSizes),
        })),
      setHeight: (height) => set({ height }),
      setSplitterSizes: (sizes) => set({ splitterSizes: sizes }),
      setCurrentEndpointId: (endpointId) =>
        set({ currentEndpointId: endpointId }),
      setSelectedFileId: (fileId) => set({ selectedFileId: fileId }),
    }),
    {
      name: BOTTOM_PANEL_STORE_NAME,
      version: BOTTOM_PANEL_PERSIST_VERSION,
      storage: createJSONStorage(() => createTenantStorage()),
      migrate: migratePersistedBottomPanelState,
      partialize: (state): PersistedBottomPanelState => ({
        selectedTab: state.selectedTab,
        height: state.height,
        splitterSizes: state.splitterSizes,
      }),
    },
  ),
);

export function migrateBottomPanelStateFromLayoutStore(): void {
  const storage = createTenantStorage();
  if (getStorageItem(BOTTOM_PANEL_LAYOUT_MIGRATION_KEY)) return;

  if (getStorageItem(BOTTOM_PANEL_STORE_NAME)) {
    storage.setItem(BOTTOM_PANEL_LAYOUT_MIGRATION_KEY, String(Date.now()));
    return;
  }

  const layoutState = readPersistedState(getStorageItem(LAYOUT_STORE_NAME));
  if (!layoutState) {
    storage.setItem(BOTTOM_PANEL_LAYOUT_MIGRATION_KEY, String(Date.now()));
    return;
  }

  const nextState: Partial<BottomPanelState> = {};

  if (isBottomPanelTab(layoutState.selectedBottomPanelTab)) {
    nextState.selectedTab = layoutState.selectedBottomPanelTab;
  }

  if (typeof layoutState.bottomPanelHeight === 'number') {
    nextState.height = layoutState.bottomPanelHeight;
  }

  if (isSplitterSizes(layoutState.bottomPanelSplitterSizes)) {
    nextState.splitterSizes = layoutState.bottomPanelSplitterSizes;
  }

  if (Object.keys(nextState).length > 0) {
    useBottomPanelStore.setState(nextState);
  }

  storage.setItem(BOTTOM_PANEL_LAYOUT_MIGRATION_KEY, String(Date.now()));
}

export const useBottomPanel = () => {
  const isOpen = useBottomPanelStore((state) => state.isOpen);
  const selectedTab = useBottomPanelStore((state) => state.selectedTab);
  const height = useBottomPanelStore((state) => state.height);
  const splitterSizes = useBottomPanelStore((state) => state.splitterSizes);
  const currentEndpointId = useBottomPanelStore(
    (state) => state.currentEndpointId,
  );
  const selectedFileId = useBottomPanelStore((state) => state.selectedFileId);
  const open = useBottomPanelStore((state) => state.open);
  const close = useBottomPanelStore((state) => state.close);
  const toggle = useBottomPanelStore((state) => state.toggle);
  const setTab = useBottomPanelStore((state) => state.setTab);
  const setHeight = useBottomPanelStore((state) => state.setHeight);
  const setSplitterSizes = useBottomPanelStore(
    (state) => state.setSplitterSizes,
  );
  const setCurrentEndpointId = useBottomPanelStore(
    (state) => state.setCurrentEndpointId,
  );
  const setSelectedFileId = useBottomPanelStore(
    (state) => state.setSelectedFileId,
  );

  const tabs = [
    {
      id: 'terminal',
      title: 'Terminal',
      type: 'terminal' as const,
      isActive: selectedTab === 'terminal',
    },
    {
      id: 'code-graph',
      title: 'Code Graph',
      type: 'code-graph' as const,
      isActive: selectedTab === 'code-graph',
    },
    {
      id: 'decoder',
      title: 'Decoder',
      type: 'decoder' as const,
      isActive: selectedTab === 'decoder',
    },
  ];

  const activeTab = tabs.find((tab) => tab.isActive);

  return {
    tabs,
    activeTab,
    activeTabId: selectedTab,
    isCollapsed: !isOpen,
    selectTab: setTab,
    closeTab: () => close(),
    toggleCollapse: toggle,
    isOpen,
    selectedTab,
    height,
    splitterSizes,
    currentEndpointId,
    selectedFileId,
    open,
    close,
    toggle,
    setTab,
    setHeight,
    setSplitterSizes,
    setCurrentEndpointId,
    setSelectedFileId,
  };
};
